import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Scale, Award, Eye, Diamond, Target, Shield,  BookOpen, Star, Trophy, CheckCircle, Lightbulb, Phone } from 'lucide-react';
import WhatsAppIcon from '../components/WhatsAppIcon';
import { Link } from 'react-router-dom';
import AnimatedNetworkBackground from '../components/AnimatedNetworkBackground';
import Header from '../components/Header';
import Footer from '../components/Footer';

const AboutUsPage = () => {
  const [isVisible, setIsVisible] = useState({});
  const [activeValue, setActiveValue] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[id^="section-"]').forEach(section => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const values = [
    { 
      icon: <Eye className="w-8 h-8" />, 
      title: "الشفافية", 
      titleEn: "Transparency", 
      desc: "نؤمن بالوضوح التام والصراحة في جميع تعاملاتنا مع العملاء، نقدم المعلومات بشكل واضح ومفهوم",
      color: "from-[#0A3A23] to-[#0A3A23]"
    },
    { 
      icon: <Shield className="w-8 h-8" />, 
      title: "الثقة", 
      titleEn: "Trust", 
      desc: "بناء علاقات طويلة الأمد مع عملائنا قائمة على الثقة المتبادلة والمصداقية في العمل",
      color: "from-[#0A3A23] to-[#0A3A23]"
    },
    { 
      icon: <Lightbulb className="w-8 h-8" />, 
      title: "الإبداع", 
      titleEn: "Creativity", 
      desc: "نقدم حلول قانونية مبتكرة وغير تقليدية تناسب احتياجات كل عميل بشكل فريد",
      color: "from-[#0A3A23] to-[#0A3A23]"
    },
    { 
      icon: <Award className="w-8 h-8" />, 
      title: "الاحترافية", 
      titleEn: "Professionalism", 
      desc: "التزام بأعلى معايير الخدمة القانونية والتطوير المستمر لمهاراتنا وخبراتنا",
      color: "from-[#0A3A23] to-[#0A3A23]"
    },
    { 
      icon: <CheckCircle className="w-8 h-8" />, 
      title: "الحماية", 
      titleEn: "Protection", 
      desc: "حماية حقوق عملائنا ومصالحهم بكل قوة وتفاني، والدفاع عنهم بأفضل الطرق القانونية",
      color: "from-[#0A3A23] to-[#0A3A23]"
    },
    { 
      icon: <Diamond className="w-8 h-8" />, 
      title: "التكيف", 
      titleEn: "Adaptability", 
      desc: "مواكبة التطورات القانونية الحديثة والتكيف مع متطلبات العصر وتحديات السوق",
      color: "from-[#0A3A23] to-[#0A3A23]"
    }
  ];



  const timeline = [
    { year: "1", event: "خطوة نحو الامان", desc: "الاستماع باهتمام" },
    { year: "2", event: "خطوة نحو الوضوح", desc: "عقد اجتماعا معك" },
    { year: "3", event: "خطوة نحو الأهداف", desc: "وضع استراتيجيات العمل" },
    { year: "4", event: "خطوة نحو النجاح", desc: "توقيع عقد الشراكة" }
  ];



  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
      
      {/* Header */}
      <Header />
      
      {/* Hero Section */}
      <section id="section-hero" className={`relative min-h-screen flex items-center justify-center overflow-hidden transition-all duration-1000 mt-20 ${isVisible['section-hero'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`} style={{ backgroundColor: '#0A3A23' }}>
        
        {/* Background Decorations */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-32 h-32 bg-gradient-to-br from-emerald-400/20 to-emerald-600/20 rounded-full animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-48 h-48 bg-gradient-to-br from-emerald-300/10 to-emerald-500/10 rounded-full animate-bounce" style={{animationDuration: '4s'}}></div>
          <div className="absolute top-1/3 right-1/4 w-20 h-20 bg-gradient-to-br from-emerald-400/20 to-emerald-600/20 rounded-full animate-ping" style={{animationDelay: '2s'}}></div>
        </div>

        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <div className="text-right">
              <div className="mb-8">
                <h1 className="text-7xl font-bold text-white mb-6 leading-tight">
                  من نحن
                </h1>
                <div className="w-32 h-2 bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full"></div>
              </div>
              
              <h2 className="text-4xl font-bold text-white mb-6 flex items-center gap-4">
                <Scale className="w-12 h-12 text-emerald-300" />
                شركة أنس عبدالرحمن خياط
              </h2>
              
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-3xl border border-white/20">
                <p className="text-xl text-white/80 leading-relaxed">
                  نقدم خدمات قانونية متخصصة ومهنية في المملكة العربية السعودية، حيث نلتزم بتقديم أفضل الخدمات القانونية لعملائنا وفقاً لأعلى المعايير المهنية والقانونية.
                </p>
              </div>
            </div>

            {/* Lawyer Image */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-96 h-[500px] bg-gradient-to-br from-emerald-400/30 to-emerald-600/30 backdrop-blur-sm rounded-3xl shadow-2xl flex items-center justify-center border-4 border-emerald-400/30 overflow-hidden">
                  <img 
                    src="/images/صور الموقع (1)/0.jpg" 
                    alt="شركة أنس للمحاماة"
                    className="w-full h-full object-cover rounded-2xl"
                  />
                </div>
                
                {/* Floating Elements */}
                <div className="absolute -top-6 -right-6 w-16 h-16 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full animate-spin shadow-xl" style={{animationDuration: '10s'}}></div>
                <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full animate-pulse shadow-xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Story Section */}
      <section id="section-story" className={`py-16 bg-white relative transition-all duration-1000 delay-300 ${isVisible['section-story'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            
            {/* Story Content - Now on the Right */}
            <div className="space-y-8">
              <div>
                <h2 className="text-5xl font-bold text-gray-800 mb-6 flex items-center gap-4">
                  <BookOpen className="w-12 h-12 text-[#0a3a23]" />
                  قصتنا
                </h2>
                <div className="w-24 h-1 rounded-full mb-8" style={{ backgroundColor: '#0A3A23' }}></div>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-r from-[#0a3a23]/5 to-[#0a3a23]/10 p-8 rounded-3xl border-r-4 border-[#0a3a23]/30 shadow-lg">
                                  <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-3">
                  <Target className="w-6 h-6 text-[#0a3a23]" />
                  البداية
                </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    بدأنا تقديم خدماتنا القانونية الشاملة في المملكة العربية السعودية، مقدمين الدعم القانوني الفعال للقطاعين العام والخاص. نلتزم في شركة أنس عبد الرحمن خياط بتوفير حلول قانونية عملية وموثوقة من خلال فريق متخصص عالي الكفاءة.
                  </p>
                </div>

                <div className="bg-gradient-to-r from-[#0a3a23]/5 to-[#0a3a23]/10 p-8 rounded-3xl border-r-4 border-[#0a3a23]/30 shadow-lg">
                                  <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-3">
                  <Eye className="w-6 h-6 text-[#0a3a23]" />
                  الرسالة
                </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    نهتم بتقديم مجموعة واسعة من الخدمات القانونية بما يشمل الاستشارات، وصياغة العقود وتمثيل العملاء أمام الجهات القضائية. كما نحرص على تقديم خدمات قانونية متكاملة للأفراد أمام المحاكم العامة ومحاكم الأحوال الشخصية وأمام المحاكم العمالية.
                  </p>
                </div>

                <div className="bg-gradient-to-r from-[#0a3a23]/5 to-[#0a3a23]/10 p-8 rounded-3xl border-r-4 border-[#0a3a23]/30 shadow-lg">
                                  <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-3">
                  <Diamond className="w-6 h-6 text-[#0a3a23]" />
                  الرؤية
                </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    بالإضافة إلى تقديم الحلول البديلة لفض النزاعات بما يتماشى مع الأنظمة السعودية، مع الحفاظ على أعلى معايير المهنية والنزاهة في جميع خدماتنا.
                  </p>
                </div>
              </div>
            </div>

            {/* Office Image - Now on the Left, aligned with البداية */}
            <div className="space-y-8">
              {/* Spacer to align with البداية */}
              <div className="h-20"></div>
              
              {/* Main Office Image */}
              <div className="relative">
                <div className="w-full bg-gradient-to-br from-[#0a3a23]/10 to-[#0a3a23]/20 rounded-3xl shadow-xl flex items-center justify-center border-4 border-[#0a3a23]/20 overflow-hidden">
                  <img 
                    src="/images/صور الموقع (1)/39.jpg"
                    alt="صورة المكتب الرئيسي"
                    className="w-full h-auto object-contain rounded-2xl"
                  />
                </div>
                <div className="absolute -top-4 -right-4 w-12 h-12 bg-[#0a3a23]/20 rounded-full animate-bounce shadow-lg"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section id="section-values" className={`py-16 bg-white relative transition-all duration-1000 delay-600 ${isVisible['section-values'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-[#0a3a23]/10 to-[#0a3a23]/20 text-[#0a3a23] px-6 py-3 rounded-full text-sm font-medium mb-6">
              <Award className="w-5 h-5" />
              قيمنا الأساسية
            </div>
            {/* <h2 className="text-5xl font-bold text-gray-900 mb-6">
              قيمنا الأساسية
            </h2> */}
            <div className="w-32 h-1 mx-auto rounded-full mb-6" style={{ backgroundColor: '#0A3A23' }}></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              نؤمن بمجموعة من القيم الأساسية التي توجه عملنا وتحدد طريقة تعاملنا مع عملائنا وشركائنا
            </p>
          </div>

          {/* Clean Values Grid */}
          <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div key={index} className="group">
                <div className="bg-gradient-to-br from-[#0a3a23]/5 to-[#0a3a23]/10 p-8 rounded-3xl border border-[#0a3a23]/20 hover:shadow-xl transition-all duration-300 cursor-pointer hover:scale-105">
                  <div className={`w-16 h-16 bg-gradient-to-br ${value.color} rounded-2xl mx-auto mb-6 flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    {value.icon}
                  </div>
                  
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-[#0A3A23] transition-colors">{value.title}</h3>
                    <p className="text-sm mb-4 font-medium" style={{ color: '#0A3A23' }}>{value.titleEn}</p>
                    <p className="text-gray-700 text-sm leading-relaxed">{value.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Simple Central Element */}
                      <div className="text-center mt-12">
            <div className="w-24 h-24 rounded-full mx-auto flex items-center justify-center shadow-2xl" style={{ backgroundColor: '#0A3A23' }}>
              <Star className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mt-6">التميز في العمل القانوني</h3>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              نلتزم بأعلى معايير الجودة والاحترافية في جميع خدماتنا القانونية
            </p>
          </div>
        </div>
      </section>
      {/* Why Choose Us Section */}
      <section id="section-why-choose-us" className={`py-16 relative overflow-hidden transition-all duration-1000 delay-900 ${isVisible['section-why-choose-us'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`} style={{ backgroundColor: '#0A3A23' }}>
        {/* Subtle Pattern Overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-32 h-32 border border-white/10 rounded-full"></div>
          <div className="absolute bottom-20 right-20 w-48 h-48 border border-white/10 rounded-full"></div>
          <div className="absolute top-1/3 right-1/4 w-20 h-20 border border-white/10 rounded-full"></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          {/* Why Choose Us Title */}
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-white mb-6">لماذا تختارنا ؟</h2>
          </div>

          {/* Call to Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a
              href="https://wa.me/966115115945?text=أريد استشارة قانونية"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-[#0a3a23] to-[#0a3a23] hover:from-[#0a3a23] hover:to-[#0a3a23] text-white px-6 py-3 rounded-xl font-semibold text-base transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              احصل على استشارة
            </a>
            <a
              href="/images/about/أنس الخياط - ملف تعريفي-5 (1).pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-[#0a3a23] to-[#0a3a23] hover:from-[#0a3a23] hover:to-[#0a3a23] text-white px-6 py-3 rounded-xl font-semibold text-base transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              الملف التعريفي
            </a>
          </div>

          {/* Divider */}
          <div className="w-full border-t-2 border-dashed border-yellow-400 mb-8"></div>
          
          {/* Scales of Justice Icon */}
          <div className="flex justify-center mb-8">
            <Scale className="w-12 h-12 text-yellow-400" />
          </div>

          {/* Why Choose Us Grid */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-400/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-yellow-400">
                <Shield className="w-8 h-8 text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">نفكر كعميل</h3>
                              <p className="text-white/80 text-sm leading-relaxed">
                  نعمل معا خطوة بخطوة، حتى نستنفذ كل السبل القانونية لحل مشكلتك، مهما كانت معقدة للحصول على افضل الحلول الممكنة.
                </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-400/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-yellow-400">
                <Users className="w-8 h-8 text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">نعمل كشريك</h3>
                              <p className="text-white/80 text-sm leading-relaxed">
                  نؤمن بأنك شريك حقيقي لنا، ونعمل جنبا إلى جنب لتحقيق أهدافك، لأن نجاحك هو نجاحنا.
                </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-400/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-yellow-400">
                <Lightbulb className="w-8 h-8 text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">نحفظ خصوصيتك</h3>
                              <p className="text-white/80 text-sm leading-relaxed">
                  خصوصيتك أولوية، ومعلوماتك في أيد أمينة ونلتزم بحمايتها بكل احترافية وضمن إطار العلاقة القانونية التي تجمعنا.
                </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section id="section-timeline" className={`py-16 bg-gradient-to-br from-gray-50 to-white transition-all duration-1000 delay-900 ${isVisible['section-timeline'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-gray-800 mb-6 flex items-center justify-center gap-4">
              <Trophy className="w-12 h-12 text-emerald-600" />
              رحلتنا معا
            </h2>
            <div className="w-32 h-1 mx-auto rounded-full mb-6" style={{ backgroundColor: '#0A3A23' }}></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              نؤمن ان رحلتنا مع كل شريك من شركائنا ستكون فريدة من نوعها، ونحرص علي تقديم الخدمات القانونية المتميزة التي تحقق لهم النجاحات
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line - Hidden on mobile */}
            <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 rounded-full" style={{ backgroundColor: '#0A3A23' }}></div>

            {/* Desktop Timeline */}
            <div className="hidden lg:block space-y-12">
              {timeline.map((item, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-16' : 'pl-16'}`}>
                    <div className={`bg-gradient-to-br from-white to-gray-50 p-8 rounded-3xl shadow-2xl border border-gray-100 hover:shadow-3xl transition-all duration-500 hover:scale-105`} style={{ borderColor: index % 2 === 0 ? '#0A3A23' : '#E5E7EB' }}>
                      <div className={`text-3xl font-bold mb-4 flex items-center gap-4 ${index % 2 === 0 ? '' : 'text-gray-600'}`} style={{ color: index % 2 === 0 ? '#0A3A23' : undefined }}>
                        <div className={`w-12 h-12 bg-gradient-to-br ${index % 2 === 0 ? 'from-[#0A3A23] to-[#0A3A23]' : 'from-gray-400 to-gray-500'} rounded-2xl flex items-center justify-center shadow-lg`}>
                          <Star className="w-6 h-6 text-white" />
                        </div>
                        <span>{item.event}</span>
                      </div>
                      <p className="text-lg text-gray-700 leading-relaxed">{item.desc}</p>
                      
                      {/* Decorative Elements */}
                      <div className="mt-6 flex items-center gap-2">
                        <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                        <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                        <div className="w-2 h-2 bg-emerald-600 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Enhanced Timeline Node with Beautiful Image */}
                  <div className="relative z-10">
                    <div className="relative">
                      {/* Glowing Effect */}
                      <div className={`absolute inset-0 w-20 h-20 rounded-full blur-xl opacity-30 animate-pulse`} style={{ backgroundColor: index % 2 === 0 ? '#0A3A23' : '#6B7280' }}></div>
                      
                      {/* Main Icon Container */}
                      <div className={`w-20 h-20 rounded-full border-4 border-white shadow-2xl relative z-10 bg-gradient-to-br ${index % 2 === 0 ? 'from-[#0A3A23] to-[#0A3A23]' : 'from-gray-400 to-gray-500'} flex items-center justify-center`}>
                        <Star className="w-8 h-8 text-white" />
                      </div>
                      
                      {/* Floating Elements */}
                      <div className={`absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full animate-bounce shadow-lg`} style={{animationDuration: '2s'}}></div>
                      <div className={`absolute -bottom-2 -left-2 w-4 h-4 bg-gradient-to-br from-emerald-300 to-emerald-500 rounded-full animate-ping`} style={{animationDuration: '3s'}}></div>
                    </div>
                  </div>
                  
                  <div className="w-1/2"></div>
                </div>
              ))}
            </div>

            {/* Mobile Timeline - Vertical Stack */}
            <div className="lg:hidden space-y-6">
              {timeline.map((item, index) => (
                <div key={index} className="relative">
                  {/* Mobile Timeline Node */}
                  <div className="flex justify-center mb-4">
                    <div className="relative">
                      {/* Glowing Effect */}
                      <div className="absolute inset-0 w-16 h-16 rounded-full blur-xl opacity-30 animate-pulse" style={{ backgroundColor: '#0A3A23' }}></div>
                      
                      {/* Main Icon Container */}
                      <div className="w-16 h-16 rounded-full border-4 border-white shadow-2xl relative z-10 bg-gradient-to-br from-[#0A3A23] to-[#0A3A23] flex items-center justify-center">
                        <Star className="w-6 h-6 text-white" />
                      </div>
                      
                      {/* Step Number */}
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-white border-2 border-[#0A3A23] rounded-full flex items-center justify-center shadow-lg">
                        <span className="text-sm font-bold text-[#0A3A23]">{item.year}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Mobile Content Card */}
                  <div className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-2xl shadow-lg border border-[#0A3A23]/20 mx-4">
                    <div className="text-center">
                      <h3 className="text-xl font-bold text-[#0A3A23] mb-3">{item.event}</h3>
                      <p className="text-gray-700 leading-relaxed">{item.desc}</p>
                      
                      {/* Decorative Elements */}
                      <div className="mt-4 flex items-center justify-center gap-2">
                        <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                        <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                        <div className="w-2 h-2 bg-emerald-600 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>



      {/* Floating Contact Buttons */}
      <a
        href="tel:920033053"
        className="fixed bottom-20 sm:bottom-28 left-4 sm:left-6 bg-gradient-to-r from-[#0a3a23] to-[#0a3a23] text-white p-3 sm:p-4 rounded-full shadow-2xl hover:shadow-[#0a3a23]/25 transition-all duration-300 z-50 hover:scale-110"
      >
        <Phone className="w-5 h-5 sm:w-6 sm:h-6" />
      </a>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/966115115945?text=أريد استشارة قانونية"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-4 sm:bottom-6 left-4 sm:left-6 bg-gradient-to-r from-[#0a3a23] to-[#0a3a23] text-white p-3 sm:p-4 rounded-full shadow-2xl hover:shadow-[#0a3a23]/25 transition-all duration-300 z-50 hover:scale-110"
      >
        <WhatsAppIcon className="w-5 h-5 sm:w-6 sm:h-6" />
      </a>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AboutUsPage; 
